<script setup>
// ORACURA: the "Bookings" pages under Conversations in the sidebar.
//
// The list itself lives on dashboard.oracura.app (/cw/bookings), which reads
// Cal.com and knows each booking's ticket. It is framed here with the same
// token the "ORACURA Orders" dashboard app already carries, taken from that
// app's URL so no secret is compiled into this image.
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';

const route = useRoute();
const router = useRouter();
const store = useStore();
const apps = useMapGetter('dashboardApps/getRecords');
// Who is working the list: completed calls and notes are credited to them.
const currentUser = useMapGetter('getCurrentUser');
const frame = ref(null);

const panel = computed(() => {
  for (const a of apps.value || []) {
    const url = (a.content && a.content[0] && a.content[0].url) || '';
    if (url.includes('/cw/orders')) return url;
  }
  return '';
});

const isDark = () => document.body.classList.contains('dark');
const kind = computed(() => (route.params.kind === 'demo' ? 'demo' : 'video'));

const src = computed(() => {
  if (!panel.value) return '';
  const u = new URL(panel.value);
  u.pathname = '/cw/bookings';
  u.searchParams.set('kind', kind.value);
  u.searchParams.set('theme', isDark() ? 'dark' : 'light');
  const me = currentUser.value || {};
  u.searchParams.set('agent', me.available_name || me.display_name || me.name || '');
  u.searchParams.set('agent_email', me.email || '');
  return u.toString();
});
const origin = computed(() => (panel.value ? new URL(panel.value).origin : ''));

// "Open ticket" inside the frame routes here instead of reloading the app.
function onMessage(e) {
  if (!origin.value || e.origin !== origin.value) return;
  const d = e.data || {};
  if (d.type === 'oracura-open-conversation' && d.id) {
    router.push({
      name: 'inbox_conversation',
      params: { accountId: route.params.accountId, conversation_id: d.id },
    });
  }
}

// Follow Chatwoot's own light/dark switch without reloading the list.
let observer;
function sendTheme() {
  const w = frame.value && frame.value.contentWindow;
  if (w && origin.value) {
    w.postMessage(
      { type: 'oracura-theme', theme: isDark() ? 'dark' : 'light' },
      origin.value
    );
  }
}

onMounted(() => {
  if (!(apps.value || []).length) store.dispatch('dashboardApps/get');
  window.addEventListener('message', onMessage);
  observer = new MutationObserver(sendTheme);
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
});
onBeforeUnmount(() => {
  window.removeEventListener('message', onMessage);
  if (observer) observer.disconnect();
});
</script>

<template>
  <section class="flex flex-col w-full h-full min-w-0 overflow-hidden bg-n-surface-1">
    <iframe
      v-if="src"
      :key="kind"
      ref="frame"
      :src="src"
      :title="kind === 'demo' ? 'Demo call bookings' : 'Video verification bookings'"
      class="w-full h-full border-0"
    />
    <div
      v-else
      class="flex items-center justify-center w-full h-full text-sm text-n-slate-11"
    >
      Loading bookings…
    </div>
  </section>
</template>
